import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AttachmentPicker } from '../components/AttachmentPicker';
import { AttachmentsPanel } from '../components/AttachmentsPanel';
import { ConfirmModal, Modal } from '../components/Modal';
import { ProductCombobox } from '../components/ProductCombobox';
import { useToast } from '../components/ToastContext';
import { cameFromDocument, createNameSeed, optionTone } from './purchaseOrderItemView.js';
import { PendingLinesBar } from './PendingLinesBar.js';
import {
  POStatus,
  formatDate,
  formatMoney,
  paymentStatusColors,
  paymentStatusLabels,
  statusColors,
  statusLabels,
} from '../lib/po-ui';

const API = import.meta.env.VITE_API_URL || '/api';

interface PurchaseOrder {
  id: string;
  poNumber: string;
  supplierId: string;
  supplierName: string;
  status: POStatus;
  paymentStatus: 'unpaid' | 'partial_paid' | 'paid';
  total: string;
  currency: string;
  expectedDate: string | null;
  estimatedPaymentDate: string | null;
  paymentMethod?: string;
  paymentDays?: number;
  sendEmail?: boolean;
  sendWhatsapp?: boolean;
  notifyManagerWhatsapp?: boolean;
  emailSentAt?: string | null;
  whatsappSentAt?: string | null;
  sendError?: string | null;
  createdAt: string;
}

interface Supplier {
  id: string;
  name: string;
  paymentTerms?: string | null;
  paymentMethod?: string;
  paymentDays?: number;
  email?: string | null;
  phone?: string | null;
  sendOcEmail?: boolean;
  sendOcWhatsapp?: boolean;
}

interface Product {
  id: string;
  name: string;
  salePrice?: number | string | null;
  costPrice?: number | string | null;
  supplierIds?: string[];
}

interface StockInfo {
  quantity: number;
  minQuantity: number;
}

type PendingConfirmAction = 'cancel_po' | 'close_po';

// Send-attempt chip for a row: delivered channels in green, delivery error in
// red. The raw backend sendError is diagnostic (not copy) — tooltip only.
function renderSendStatus(po: PurchaseOrder) {
  if (po.sendError) {
    return (
      <span
        title={po.sendError}
        className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"
      >
        ⚠️ No se pudo enviar
      </span>
    );
  }
  if (!po.emailSentAt && !po.whatsappSentAt) return null;
  const parts: string[] = [];
  if (po.emailSentAt) parts.push('📧 Enviado');
  if (po.whatsappSentAt) parts.push('💬 Enviado');
  return (
    <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
      {parts.join(' · ')}
    </span>
  );
}

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  efectivo: 'Efectivo', transferencia: 'Transferencia', cheque: 'Cheque', tarjeta: 'Tarjeta', otro: 'Otro',
};

function paymentConditionLabel(s: { paymentMethod?: string; paymentDays?: number; paymentTerms?: string | null }): string {
  const method = PAYMENT_METHOD_LABEL[s.paymentMethod || 'transferencia'] || 'Transferencia';
  const days = Number(s.paymentDays ?? -1);
  const moment = days === 0
    ? 'Contra entrega'
    : days === 7 ? '1 semana'
    : days === 15 ? '15 días'
    : days === 30 ? '30 días'
    : days === 45 ? '45 días'
    : days === 60 ? '60 días'
    : days === 90 ? '90 días'
    : days > 0 ? `${days} días`
    : (s.paymentTerms || '-');
  return `${method} · ${moment}`;
}

/**
 * Linea del formulario de orden de compra.
 *
 * `productId` vacio significa que la linea **todavia no apunta a un producto** y
 * hay que resolverla antes de guardar: o se elige uno del stock, o se crea el
 * articulo ahi mismo — en cantidad 0, y la recepcion despues suma lo que llegue.
 * Una orden nunca se guarda con una linea sin producto: no se podria recibir.
 *
 * `matchKind` es la confianza de la lectura automatica, que es lo que pinta la
 * fila: 'stock' ya lo encontro en el stock, 'catalog' lo encontro en el catalogo
 * del rubro (verde), 'none' no lo encontro en ningun lado (ambar, hay que mirarlo).
 */
/** Otro candidato razonable, del stock o del catálogo, para que elija el usuario. */
interface MatchAlternative {
  source: 'stock' | 'catalog';
  productId: string | null;
  catalogItemId: string | null;
  name: string;
  brand: string | null;
  score: number;
  confidence: 'high' | 'review';
  missingWords: string[];
}

type OrderItemForm = {
  productId: string;
  matchKind?: 'stock' | 'catalog' | 'none';
  /**
   * Confianza del match: `high` cuando el candidato explica todas las palabras del
   * documento (o vino por codigo de barras/SKU). `review` cuando hay dudas — la
   * linea se muestra para revisar y hay que confirmarla, nunca se da por resuelta.
   */
  confidence?: 'high' | 'review';
  /** Palabras del documento que el candidato no explica. Es el "por que no". */
  missingWords?: string[];
  /** Otros candidatos plausibles: los dos lados compiten, elige el comercio. */
  candidates?: MatchAlternative[];
  /** El usuario ya miro una linea en revision. */
  reviewed?: boolean;
  /** Item del catalogo del rubro, cuando `matchKind` es 'catalog'. */
  catalogItemId?: string;
  /** Nombre que propone la lectura o el catalogo, para prellenar el alta. */
  matchName?: string;
  /** Lo que decia el documento, tal como se leyo. Trazabilidad de la lectura. */
  description?: string;
  barcode?: string;
  quantity: string;
  unitCost: string;
  discountPct: string;
  taxRate: string;
};

export function PurchaseOrders() {
  const { notify } = useToast();
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [stockLevels, setStockLevels] = useState<Record<string, StockInfo>>({});
  const [salesData, setSalesData] = useState<Record<string, number>>({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);

  // Server-driven pagination (FR-SUP-006): page/total come from the API;
  // `total` is the tenant-wide count honoring q+status (never orders.length).
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendTarget, setSendTarget] = useState<PurchaseOrder | null>(null);
  const [sendAttachment, setSendAttachment] = useState<File | null>(null);
  const [sendSending, setSendSending] = useState(false);
  const [sendChannels, setSendChannels] = useState({ sendEmail: true, sendWhatsapp: true, notifyManagerWhatsapp: false });
  const [pendingDocFile, setPendingDocFile] = useState<File | null>(null);
  const [attachmentsFor, setAttachmentsFor] = useState<PurchaseOrder | null>(null);
  // Accessible confirm dialog state for the cancel/close actions (replaces
  // the native dialogs).
  const [pendingConfirm, setPendingConfirm] = useState<{
    action: PendingConfirmAction;
    poId: string;
  } | null>(null);

  const [statusFilter, setStatusFilter] = useState<POStatus | 'all'>('all');
  const [search, setSearch] = useState('');

  // Form state
  // Lectura automatica del documento (foto o PDF) que da origen a la orden.
  // El servidor devuelve un borrador: nada se guarda hasta que el usuario confirme.
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importInfo, setImportInfo] = useState<{
    documentNumber: string | null;
    documentDate: string | null;
    supplier: string | null;
    total: number | null;
    warnings: string[];
    needsAttention: { description: string; kind: 'catalog' | 'none' }[];
  } | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);
  // La propuesta completa se manda al crear la orden, para poder medir despues
  // cuantas veces el usuario tuvo que corregirla.
  const [importedDraft, setImportedDraft] = useState<unknown>(null);

  const [form, setForm] = useState({
    supplierId: '',
    expectedDate: '',
    notes: '',
    currency: 'ARS',
    exchangeRate: '1',
    freight: '0',
    generalDiscountPct: '0',
    sendEmail: true,
    sendWhatsapp: true,
    notifyManagerWhatsapp: false,
    items: [{ productId: '', quantity: '1', unitCost: '0', discountPct: '0', taxRate: '21' }] as OrderItemForm[],
  });

  /**
   * Alta del producto de una linea, en el mismo formulario.
   *
   * Por que aca y no en la recepcion: corregir el texto **con el documento a la
   * vista** es cuando el usuario sabe que dice. Arrastrar una lectura mala hasta
   * la recepcion obliga a resolver tarde y sin el papel.
   */
  const [creatingLine, setCreatingLine] = useState<number | null>(null);
  const [createForm, setCreateForm] = useState({ name: '', brand: '', salePrice: '', barcode: '' });
  const [createBusy, setCreateBusy] = useState(false);
  /** Línea con el buscador manual abierto (para elegir un producto a mano). */
  const [manualSearch, setManualSearch] = useState<number | null>(null);
  /** La lectura de archivos se ofrece recién cuando el usuario la busca. */
  const [showImport, setShowImport] = useState(false);
  /** Alta del proveedor leído sin salir del formulario. */
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: '', taxId: '', phone: '', email: '' });
  const [supplierBusy, setSupplierBusy] = useState(false);
  /** Producto cuyo vínculo con el proveedor se está guardando. */
  const [associatingProductId, setAssociatingProductId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<{
    stock: { id: string; name: string; supplierIds: string[] }[];
    catalog: { id: string; name: string }[];
  }>({ stock: [], catalog: [] });

  const token = localStorage.getItem('accessToken');
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
  };

  async function loadOrders(opts: { page?: number; append?: boolean } = {}) {
    const targetPage = opts.page ?? 1;
    try {
      const statusQuery = statusFilter !== 'all' ? `&status=${statusFilter}` : '';
      const res = await fetch(`${API}/purchase-orders?page=${targetPage}&limit=50&q=${encodeURIComponent(search)}${statusQuery}`, { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al cargar las órdenes de compra');
      }
      const data = (await res.json()) as { data?: PurchaseOrder[]; total?: number };
      if (opts.append) {
        // Append mode: next batch of 50 (no client-side re-filter/dupes)
        setOrders((prev) => [...prev, ...(data.data || [])]);
      } else {
        setOrders(data.data || []);
      }
      setTotal(data.total ?? 0);
    } catch (e: any) {
      console.error('PurchaseOrders load error:', e);
      // Keep the existing inline banner — NO toast for list/load errors (TNT-006)
      setError(e.message || 'Error al cargar las órdenes de compra');
    }
  }

  async function loadStaticData() {
    setLoading(true);
    setError(null);
    try {
      const [supRes, prodRes, stockRes, salesRes] = await Promise.all([
        fetch(`${API}/suppliers`, { headers }),
        fetch(`${API}/products`, { headers }),
        fetch(`${API}/stock/levels`, { headers }),
        fetch(`${API}/reports/top-products`, { headers }),
      ]);

      if (supRes.ok) {
        const d = (await supRes.json()) as { data?: Supplier[] };
        setSuppliers(d.data || []);
      }
      if (prodRes.ok) {
        const d = (await prodRes.json()) as { data?: Product[] };
        setProducts(d.data || []);
      }
      if (stockRes.ok) {
        const d = (await stockRes.json()) as { data?: any[] };
        const map: Record<string, StockInfo> = {};
        (d.data || []).forEach((s: any) => {
          map[s.productId] = { quantity: Number(s.quantity), minQuantity: Number(s.minQuantity) };
        });
        setStockLevels(map);
      }
      if (salesRes.ok) {
        const d = (await salesRes.json()) as { data?: any[] };
        const map: Record<string, number> = {};
        (d.data || []).forEach((s: any) => {
          map[s.productId] = Number(s.totalQuantity);
        });
        setSalesData(map);
      }

      await loadOrders();
    } catch (e: any) {
      console.error('PurchaseOrders static load error:', e);
      setError(e.message || 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadStaticData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search × status filter = ONE debounced effect (Customers.tsx:66-82 pattern).
  // Both inputs reset the page and trigger a server query; a typing burst
  // produces exactly one request 300ms after the last keystroke. First run is
  // skipped — the mount request comes from loadStaticData().
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    const handler = setTimeout(() => {
      setPage(1);
      loadOrders({ page: 1, append: false });
    }, 300);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  const selectedSupplier = useMemo(
    () => suppliers.find((s) => s.id === form.supplierId),
    [suppliers, form.supplierId]
  );

  // Sort products: no stock → low stock → in stock, by sales desc
  const sortedProducts = useMemo(() => {
    return [...products].sort((a, b) => {
      const stockA = stockLevels[a.id]?.quantity ?? 0;
      const minA = stockLevels[a.id]?.minQuantity ?? 0;
      const stockB = stockLevels[b.id]?.quantity ?? 0;
      const minB = stockLevels[b.id]?.minQuantity ?? 0;
      const groupA = stockA <= 0 ? 0 : stockA <= minA ? 1 : 2;
      const groupB = stockB <= 0 ? 0 : stockB <= minB ? 1 : 2;
      if (groupA !== groupB) return groupA - groupB;
      return (salesData[b.id] ?? 0) - (salesData[a.id] ?? 0);
    });
  }, [products, stockLevels, salesData]);

  /**
   * Filtrar el combobox a los productos del proveedor elegido.
   *
   * Tildado por defecto: cuando le compras siempre al mismo proveedor, acota la
   * lista y acelera. Pero es una eleccion VISIBLE, porque escondido empuja a crear
   * un duplicado de un producto que ya existe con otro proveedor.
   *
   * Las sugerencias de texto NO usan este filtro: ahi se busca similitud, y una
   * coincidencia escondida es justamente lo que hay que mostrar.
   */
  const [onlySupplierProducts, setOnlySupplierProducts] = useState(true);

  const visibleProducts = useMemo(() => {
    if (!onlySupplierProducts || !form.supplierId) return sortedProducts;
    const ofSupplier = sortedProducts.filter((p) => (p.supplierIds || []).includes(form.supplierId));

    // Los productos YA elegidos en alguna linea viajan siempre, aunque no sean del
    // proveedor: el combobox busca su valor dentro de esta lista, y si no lo
    // encuentra muestra el campo vacio en una linea que SI tiene producto.
    // El filtro es para ELEGIR, no para ocultar lo ya elegido.
    const chosen = new Set(form.items.map((i) => i.productId).filter(Boolean));
    const alreadyChosen = sortedProducts.filter((p) => chosen.has(p.id) && !ofSupplier.includes(p));

    return [...ofSupplier, ...alreadyChosen];
  }, [sortedProducts, form.supplierId, onlySupplierProducts, form.items]);

  function getStockInfo(productId: string) {
    const stock = stockLevels[productId]?.quantity ?? 0;
    const min = stockLevels[productId]?.minQuantity ?? 0;
    if (stock <= 0) return { color: '#dc2626', label: 'Sin stock', stock };
    if (stock <= min) return { color: '#f59e0b', label: 'Bajo mínimo', stock };
    return { color: '#16a34a', label: 'En stock', stock };
  }

  function calcItem(item: (typeof form.items)[0]) {
    const qty = Number(item.quantity) || 0;
    const cost = Number(item.unitCost) || 0;
    const discPct = Number(item.discountPct) || 0;
    const taxRate = Number(item.taxRate) || 0;
    const gross = qty * cost;
    const discount = gross * (discPct / 100);
    const net = gross - discount;
    const tax = net * (taxRate / 100);
    const total = net + tax;
    return { gross, discount, net, tax, total };
  }

  const calc = useMemo(() => {
    return form.items.reduce(
      (acc, item) => {
        const c = calcItem(item);
        return {
          subtotal: acc.subtotal + c.gross,
          itemDiscount: acc.itemDiscount + c.discount,
          taxTotal: acc.taxTotal + c.tax,
          lineTotal: acc.lineTotal + c.total,
        };
      },
      { subtotal: 0, itemDiscount: 0, taxTotal: 0, lineTotal: 0 }
    );
  }, [form.items]);

  const genDiscountPct = Number(form.generalDiscountPct) || 0;
  const genDiscount = (calc.subtotal - calc.itemDiscount) * (genDiscountPct / 100);
  const freight = Number(form.freight) || 0;
  const grandTotal = calc.subtotal - calc.itemDiscount - genDiscount + calc.taxTotal + freight;

  /**
   * Lee un remito o presupuesto y prellena el formulario.
   *
   * El resultado es un BORRADOR: el usuario revisa y corrige antes de guardar. La
   * lectura automatica acierta seguido pero no siempre, y una cantidad equivocada
   * en una orden de compra cuesta plata.
   */
  async function importFromDocument(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite volver a elegir el mismo archivo
    if (!file) return;

    setImporting(true);
    setImportError(null);
    setImportInfo(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/purchase-orders/extract`, {
        method: 'POST',
        headers: {
          Authorization: headers.Authorization,
          'x-tenant-slug': headers['x-tenant-slug'],
        },
        body: fd,
      });
      const d = await res.json().catch(() => ({}));
      // El limite de tamano lo corta el servidor antes de llegar al codigo de la
      // ruta, y responde en ingles: se traduce aca, que es donde el usuario lo lee.
      if (res.status === 413) {
        throw new Error('El archivo es demasiado grande (máximo 20 MB). Probá con una foto más chica.');
      }
      if (!res.ok) throw new Error(d.error || 'No se pudo leer el documento');

      const draft = d.data ?? {};
      const items: any[] = Array.isArray(draft.items) ? draft.items : [];

      setForm((f) => ({
        ...f,
        supplierId: draft.matchedSupplierId || f.supplierId,
        notes: draft.notes ? (f.notes ? `${draft.notes}\n${f.notes}` : draft.notes) : f.notes,
            items: items.length
          ? items.map((i) => ({
              // El documento leyó un TEXTO, no eligió un producto: la línea entra SIN
              // producto y con las opciones a la vista. Preseleccionar sería afirmar
              // una certeza que la lectura no tiene.
              productId: '',
              matchKind: (i.match?.kind ?? 'none') as 'stock' | 'catalog' | 'none',
              confidence: (i.match?.confidence ?? 'review') as 'high' | 'review',
              missingWords: i.match?.missingWords ?? [],
              candidates: [
                ...(i.match && i.match.kind !== 'none' && i.match.matchedName
                  ? [{
                      source: i.match.kind as 'stock' | 'catalog',
                      productId: i.match.productId,
                      catalogItemId: i.match.catalogItemId,
                      name: i.match.matchedName,
                      brand: null,
                      score: i.match.score ?? 0,
                      confidence: (i.match.confidence ?? 'review') as 'high' | 'review',
                      missingWords: i.match.missingWords ?? [],
                    }]
                  : []),
                ...(i.match?.alternatives ?? []),
              ],
              catalogItemId: i.match?.catalogItemId || undefined,
              matchName: i.match?.matchedName || i.description || undefined,
              // Lo que decía el documento se conserva siempre, como trazabilidad.
              description: i.description || undefined,
              barcode: i.code || undefined,
              quantity: String(i.quantity ?? 1),
              unitCost: i.unitCost === null || i.unitCost === undefined ? '' : String(i.unitCost),
              discountPct: '0',
              // La alícuota viene deducida del documento: si el precio ya trae el
              // IVA, la línea va en 0 y el total cierra con el papel.
              taxRate: String(i.taxRate ?? 21),
            }))
          : f.items,
      }));

      setImportedDraft(draft);

      setImportInfo({
        documentNumber: draft.documentNumber ?? null,
        documentDate: draft.documentDate ?? null,
        supplier: draft.supplier ?? null,
        total: draft.total ?? null,
        warnings: Array.isArray(draft.warnings) ? draft.warnings : [],
        needsAttention: items
          .filter((i) => i.match?.kind === 'catalog' || i.match?.kind === 'none')
          .map((i) => ({ description: i.description, kind: i.match?.kind })),
      });

      // El documento que se leyo queda adjunto a la orden al guardarla, reusando
      // el mismo mecanismo que la documentacion adjunta a mano: asi queda el
      // respaldo de donde salieron los datos.
      setPendingDocFile(file);

      if (items.length) {
        notify({ type: 'success', message: `Se leyeron ${items.length} productos del documento. Revisalos antes de guardar.` });
      } else {
        notify({ type: 'error', message: 'No se detectaron productos en el documento.' });
      }
    } catch (err: any) {
      setImportError(err.message || 'No se pudo leer el documento');
      notify({ type: 'error', message: err.message || 'No se pudo leer el documento' });
    } finally {
      setImporting(false);
    }
  }

  function addItem() {
    setForm((f) => ({
      ...f,
      items: [...f.items, { productId: '', quantity: '1', unitCost: '0', discountPct: '0', taxRate: '21' }],
    }));
  }

  function removeItem(idx: number) {
    if (form.items.length <= 1) return;
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  }

  function updateItem(idx: number, field: string, value: string) {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...f, items };
    });
  }

  function onProductChange(idx: number, productId: string) {
    const prod = products.find((p) => p.id === productId);
    setForm((f) => {
      const items = [...f.items];
      items[idx] = {
        ...items[idx],
        productId,
        // Elegido a mano: la linea queda resuelta y deja de estar en revision.
        matchKind: 'stock',
        // Si el producto ya tiene costo cargado, manda sobre lo que leyó el documento.
        ...(prod?.costPrice ? { unitCost: String(prod.costPrice) } : {}),
      };
      return { ...f, items };
    });
    // Elegir un producto cierra el alta si estaba abierta para esta linea.
    setCreatingLine((current) => (current === idx ? null : current));
  }

  /** Deja la linea apuntando a un producto existente. */
  function linkLineToProduct(idx: number, productId: string) {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], productId, matchKind: 'stock' };
      return { ...f, items };
    });
    setCreatingLine(null);
  }

  /**
   * El usuario eligio otro candidato de los que se le ofrecieron.
   *
   * Del stock: se vincula y listo. Del catalogo: hay que crear el producto, asi que
   * se abre el alta con ese item precargado (el precio lo define el usuario).
   */
  function chooseAlternative(idx: number, alt: MatchAlternative) {
    if (alt.source === 'stock' && alt.productId) {
      setForm((f) => {
        const items = [...f.items];
        items[idx] = {
          ...items[idx],
          productId: alt.productId!,
          matchKind: 'stock',
          confidence: 'high',
          missingWords: [],
          candidates: [],
          reviewed: true,
        };
        return { ...f, items };
      });
      notify({ type: 'success', message: `Línea vinculada a ${alt.name}.` });
      return;
    }

    setForm((f) => {
      const items = [...f.items];
      items[idx] = {
        ...items[idx],
        matchKind: 'catalog',
        catalogItemId: alt.catalogItemId ?? undefined,
        matchName: alt.name,
      };
      return { ...f, items };
    });
    openCreate(idx, { name: alt.name });
    notify({ type: 'success', message: `Elegiste «${alt.name}» del catálogo: definí el precio y creá el producto.` });
  }

  /**
   * Abre el documento leído en una ventana aparte, para ponerlo al lado y comparar
   * mientras se corrigen los datos. Se abre el archivo local: la orden todavía no
   * existe, así que no hay adjunto al que apuntar.
   */
  /**
   * Alta del proveedor leído, sin salir del formulario.
   *
   * Si el documento trae un nombre que no está en la lista, mandar a Proveedores,
   * crearlo y volver haría perder todo lo leído. Se crea acá y la orden sigue.
   */
  async function createSupplierFromDocument() {
    if (!newSupplier.name.trim()) {
      notify({ type: 'error', message: 'Poné el nombre del proveedor.' });
      return;
    }
    setSupplierBusy(true);
    try {
      const res = await fetch(`${API}/suppliers`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: newSupplier.name.trim(),
          taxId: newSupplier.taxId.trim() || undefined,
          phone: newSupplier.phone.trim() || undefined,
          email: newSupplier.email.trim() || undefined,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { data?: { id: string; name: string }; error?: string };
      if (!res.ok) throw new Error(data.error || 'No se pudo crear el proveedor');
      const created = data.data;
      if (!created?.id) throw new Error('La respuesta no trajo el proveedor creado');
      setForm((f) => ({ ...f, supplierId: created.id }));
      setShowSupplierModal(false);
      await loadStaticData();
      notify({ type: 'success', message: `${created.name} agregado a tus proveedores.` });
    } catch (e: any) {
      notify({ type: 'error', message: e.message || 'No se pudo crear el proveedor' });
    } finally {
      setSupplierBusy(false);
    }
  }

  function openDocumentWindow() {
    if (!pendingDocFile) {
      notify({ type: 'success', message: 'El documento queda adjunto a la orden cuando la guardás.' });
      return;
    }
    const url = URL.createObjectURL(pendingDocFile);
    window.open(url, '_blank', 'noopener');
  }

  /** Abre el alta para una linea, con lo que ya sabemos prellenado. */
  function openCreate(idx: number, override?: { name?: string }) {
    const item = form.items[idx];
    setCreatingLine(idx);
    setSuggestions({ stock: [], catalog: [] });
    setCreateForm({
      // El nombre sale de lo que LEYÓ el documento. Si se está creando un producto
      // es porque ningún candidato sirve, así que proponer el de uno descartado
      // sería ponerle a este producto el nombre de otro.
      name: createNameSeed(item, override),
      brand: '',
      // Costo + 40%: el mismo margen por defecto que usa el backend. Lo puede
      // cambiar el dueño; lo que no puede pasar es que quede en 0 y se venda a 0.
      salePrice: Number(item.unitCost) > 0 ? (Number(item.unitCost) * 1.4).toFixed(2) : '',
      barcode: item.barcode || '',
    });
  }

  /**
   * Crea el producto de la linea y la deja apuntando a el.
   *
   * Si la linea (o la sugerencia elegida) viene del catalogo del rubro, se usa
   * `catalog/copy`: trae marca, codigo y unidades del catalogo, y ademas rechaza
   * el duplicado si ese item ya estaba copiado, devolviendo el producto que ya
   * existe en vez de crear otro igual.
   */
  async function createProductForLine(idx: number, override?: { catalogItemId: string; name: string }) {
    const item = form.items[idx];
    const catalogItemId = override?.catalogItemId
      ?? (item.matchKind === 'catalog' ? item.catalogItemId : undefined);
    const name = (override?.name ?? createForm.name).trim();
    const salePrice = Number(createForm.salePrice);

    if (!name) {
      notify({ type: 'error', message: 'Poné el nombre del producto.' });
      return;
    }
    if (!(salePrice > 0)) {
      notify({
        type: 'error',
        message: 'El precio de venta es obligatorio y tiene que ser mayor a 0: si queda en 0 se puede vender a $0.',
      });
      return;
    }

    setCreateBusy(true);
    try {
      const res = await fetch(catalogItemId ? `${API}/catalog/copy` : `${API}/products`, {
        method: 'POST',
        headers,
        body: JSON.stringify(catalogItemId
          ? {
              catalogItemId,
              name,
              salePrice: salePrice.toFixed(2),
              costPrice: item.unitCost || null,
            }
          : {
              name,
              brand: createForm.brand.trim() || undefined,
              barcode: createForm.barcode.trim() || undefined,
              salePrice: salePrice.toFixed(2),
              costPrice: item.unitCost || undefined,
            }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        data?: { id?: string; name?: string };
        error?: string;
        existingProductId?: string;
      };

      if (!res.ok) {
        // "Ya lo tenias" no es un error para el usuario: es la respuesta util.
        // Se vincula la linea a ese producto en vez de crear un duplicado.
        if (data.existingProductId) {
          linkLineToProduct(idx, data.existingProductId);
          notify({
            type: 'success',
            message: 'Ese producto ya estaba en tu lista: la línea quedó vinculada a él.',
          });
          return;
        }
        throw new Error(data.error || 'No se pudo crear el producto');
      }
      const created = data.data;
      if (!created?.id) throw new Error('La respuesta no trajo el producto creado');

      linkLineToProduct(idx, created.id);
      // El producto nuevo tiene que aparecer en el buscador y en la lista.
      await loadStaticData();
      notify({
        type: 'success',
        message: `${created.name || name} agregado al stock: entra en 0 hasta que se reciba.`,
      });
    } catch (e: any) {
      notify({ type: 'error', message: e.message || 'No se pudo crear el producto' });
    } finally {
      setCreateBusy(false);
    }
  }

  /**
   * Busca parecidos mientras se escribe el nombre: en el stock del comercio
   * (activos E inactivos, porque un producto desactivado sigue siendo el mismo
   * articulo) y en el catalogo del rubro.
   *
   * Es lo que evita crear un producto que ya existe con otro nombre, que es la
   * causa numero uno de duplicados. El catalogo ya viene sin lo que el comercio
   * tiene: `searchCatalog` excluye por codigo de barras y SKU.
   */
  useEffect(() => {
    if (creatingLine === null) return;
    const q = createForm.name.trim();
    if (q.length < 3) {
      setSuggestions({ stock: [], catalog: [] });
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const [stockRes, catalogRes] = await Promise.all([
          fetch(`${API}/products?q=${encodeURIComponent(q)}&limit=5&includeInactive=true`, { headers }),
          fetch(`${API}/catalog/search?q=${encodeURIComponent(q)}&limit=5`, { headers }),
        ]);
        if (cancelled) return;
        const stockData = stockRes.ok
          ? ((await stockRes.json()) as { data?: { id: string; name: string; supplierIds?: string[] }[] })
          : {};
        const catalogData = catalogRes.ok
          ? ((await catalogRes.json()) as { data?: { hits?: { id: string; name: string }[] } })
          : {};
        if (cancelled) return;
        setSuggestions({
          stock: (stockData.data || []).map((p) => ({
            id: p.id,
            name: p.name,
            supplierIds: p.supplierIds || [],
          })),
          catalog: (catalogData.data?.hits || []).map((h) => ({ id: h.id, name: h.name })),
        });
      } catch {
        // Una busqueda de sugerencias que falla no puede romper el formulario:
        // el usuario igual puede elegir a mano o crear el producto.
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [createForm.name, creatingLine]);


  /**
   * Asocia el proveedor de la orden al producto de una linea, cuando el usuario
   * lo confirma ahi mismo.
   *
   * Usa `POST /products/:id/suppliers` y NO el PUT de productos: el PUT reemplaza
   * las asociaciones, asi que mandarlo con este solo proveedor le borraria al
   * producto todos los demas.
   */
  async function associateSupplierToProduct(idx: number, productId: string) {
    if (!form.supplierId) return;
    setAssociatingProductId(productId);
    try {
      const res = await fetch(`${API}/products/${productId}/suppliers`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          supplierId: form.supplierId,
          // El costo de esta orden es lo que este proveedor cobra por el producto.
          costPrice: form.items[idx]?.unitCost || null,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string; created?: boolean };
      if (!res.ok) throw new Error(data.error || 'No se pudo asociar el proveedor');

      // Refrescar: el producto ahora trae al proveedor y la nota de la linea se va.
      await loadStaticData();
      notify({
        type: 'success',
        message: data.created === false
          ? 'Ese proveedor ya estaba asociado al producto.'
          : 'Proveedor asociado al producto.',
      });
    } catch (e: any) {
      notify({ type: 'error', message: e.message || 'No se pudo asociar el proveedor' });
    } finally {
      setAssociatingProductId(null);
    }
  }

  /**
   * Quita del pedido las lineas que quedaron sin resolver.
   *
   * Es explicito y dice cuantas son: descartar lineas en silencio haria que el
   * comercio pida menos de lo que queria y no se entere.
   */
  function removeUnresolvedItems() {
    const withProduct = form.items.filter((i) => i.productId);
    const pending = form.items.length - withProduct.length;
    if (!withProduct.length) {
      notify({ type: 'error', message: 'El pedido necesita al menos una línea con producto.' });
      return;
    }
    setForm((f) => ({ ...f, items: f.items.filter((i) => i.productId) }));
    setCreatingLine(null);
    notify({
      type: 'success',
      message: `Quité ${pending} ${pending === 1 ? 'línea' : 'líneas'} sin producto del pedido.`,
    });
  }

  function resetForm() {
    // Limpiar tambien el panel: si no, al abrir una orden nueva seguia mostrando
    // el documento de la orden anterior.
    setImportedDraft(null);
    setImportInfo(null);
    setImportError(null);
    setForm({
      supplierId: '',
      expectedDate: '',
      notes: '',
      currency: 'ARS',
      exchangeRate: '1',
      freight: '0',
      generalDiscountPct: '0',
      sendEmail: true,
      sendWhatsapp: true,
      notifyManagerWhatsapp: false,
    items: [{ productId: '', quantity: '1', unitCost: '0', discountPct: '0', taxRate: '21' }] as OrderItemForm[],
    });
  }

  async function create(e: React.FormEvent, sendNow: boolean) {
    e.preventDefault();
    // Cada causa con su propio mensaje. Antes habia uno solo que decia "al menos
    // un producto con cantidad mayor a 0": con las lineas sin catalogar ya
    // permitidas, ese texto mandaba a buscar el problema por el lado equivocado.
    if (!form.supplierId) {
      notify({ type: 'error', message: 'Elegí el proveedor de la orden.' });
      return;
    }
    // Toda linea de una orden ES un producto del stock: si el articulo no
    // existia, se crea aca (en cantidad 0) y la recepcion suma lo que llegue.
    // Una linea sin producto no se puede recibir.
    const sinProducto = form.items.findIndex((i) => !i.productId);
    if (sinProducto !== -1) {
      notify({
        type: 'error',
        message: `La línea ${sinProducto + 1} todavía no es un producto del stock: agregala al stock, elegí un producto o quitala del pedido.`,
      });
      return;
    }
    // (El chequeo de lineas "a revisar" que vivia aca se elimino con el barrido: en
    // el flujo nuevo el aviso de revisar ya no existe como pantalla, asi que puede
    // quedar una linea resuelta con confidence 'review' y sin confirmar — y esto la
    // bloqueaba sin darle al usuario ninguna forma de destrabarla. La duda ya se
    // resuelve antes, cuando se elige entre las opciones sugeridas.)
    const lineaSinCantidad = form.items.findIndex((i) => !(Number(i.quantity) > 0));
    if (lineaSinCantidad !== -1) {
      notify({
        type: 'error',
        message: `La línea ${lineaSinCantidad + 1} quedó sin cantidad: el documento no la traía legible. Completala y volvé a guardar.`,
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/purchase-orders`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          supplierId: form.supplierId,
          items: form.items.map((i) => ({
            productId: i.productId,
            // Lo que decía el documento viaja siempre: es la trazabilidad de la
            // lectura automática, para compararla después contra el producto real.
            description: i.description,
            barcode: i.barcode,
            quantity: Number(i.quantity),
            unitCost: i.unitCost,
            discountPct: i.discountPct,
            taxRate: i.taxRate,
          })),
          expectedDate: form.expectedDate || undefined,
          notes: form.notes || undefined,
          currency: form.currency,
          exchangeRate: form.exchangeRate,
          freight: form.freight,
          generalDiscountPct: form.generalDiscountPct,
          sendEmail: form.sendEmail,
          sendWhatsapp: form.sendWhatsapp,
          notifyManagerWhatsapp: form.notifyManagerWhatsapp,
          extractionDraft: importedDraft ?? undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al crear la orden');
      }
      const data = (await res.json()) as { data?: PurchaseOrder };
      let sendFailed = false;
      if (sendNow && data.data?.id) {
        const sr = await fetch(`${API}/purchase-orders/${data.data.id}/send`, {
          method: 'POST',
          headers: { Authorization: headers.Authorization, 'x-tenant-slug': headers['x-tenant-slug'] },
        });
        if (!sr.ok) {
          const se = await sr.json().catch(() => ({}));
          notify({ type: 'error', message: 'La orden se creó, pero no se pudo enviar' });
          sendFailed = true;
        }
      }
      if (!sendFailed) {
        notify({ type: 'success', message: sendNow ? 'Orden creada y enviada al proveedor' : 'Orden creada' });
      }
      // Si el usuario adjuntó documentación en el alta, subirla como paso "order"
      if (data.data?.id && pendingDocFile) {
        try {
          const fd = new FormData();
          fd.append('file', pendingDocFile);
          const up = await fetch(`${API}/purchase-orders/${data.data.id}/attachments?category=order`, {
            method: 'POST',
            headers: { Authorization: headers.Authorization, 'x-tenant-slug': headers['x-tenant-slug'] },
            body: fd,
          });
          if (!up.ok) {
            const ude = await up.json().catch(() => ({}));
            throw new Error(ude.error || 'Error al subir');
          }
        } catch (ue: any) {
          notify({ type: 'error', message: 'La orden se creó, pero no se pudo adjuntar el documento' });
        }
      }
      setPendingDocFile(null);
      setShowForm(false);
      resetForm();
      await loadOrders();
    } catch (e: any) {
      notify({ type: 'error', message: 'No se pudo crear la orden' });
    } finally {
      setSubmitting(false);
    }
  }

  async function uploadAttachment(poId: string, category: string, file: File) {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API}/purchase-orders/${poId}/attachments?category=${category}`, {
      method: 'POST',
      headers: { Authorization: headers.Authorization, 'x-tenant-slug': headers['x-tenant-slug'] },
      body: fd,
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.error || 'Error al subir');
    }
    return res.json();
  }

  async function confirmSend() {
    if (!sendTarget) return;
    setSendSending(true);
    try {
      // Persistir el snapshot de canales elegido en el modal antes de enviar
      const cr = await fetch(`${API}/purchase-orders/${sendTarget.id}/channels`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(sendChannels),
      });
      if (!cr.ok) {
        const ce = await cr.json().catch(() => ({}));
        throw new Error(ce.error || 'Error al guardar los canales de envío');
      }
      const res = await fetch(`${API}/purchase-orders/${sendTarget.id}/send`, {
        method: 'POST',
        headers: { Authorization: headers.Authorization, 'x-tenant-slug': headers['x-tenant-slug'] },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al enviar');
      }
      notify({ type: 'success', message: 'Orden enviada al proveedor' });
      if (sendAttachment) {
        try {
          await uploadAttachment(sendTarget.id, 'send', sendAttachment);
          notify({ type: 'success', message: 'Comprobante adjuntado' });
        } catch (ae: any) {
          notify({ type: 'error', message: 'No se pudo adjuntar el comprobante' });
        }
      }
      setSendTarget(null);
      setSendAttachment(null);
      await loadOrders();
    } catch (e: any) {
      notify({ type: 'error', message: 'No se pudo enviar la orden' });
    } finally {
      setSendSending(false);
    }
  }

  // Native dialogs replaced by the accessible ConfirmModal (#2): these are
  // openers; execution happens in executeCancelPO/executeClosePO.
  function cancelPO(id: string) {
    setPendingConfirm({ action: 'cancel_po', poId: id });
  }

  async function executeCancelPO(id: string) {
    setActionBusy(`cancel-${id}`);
    try {
      const res = await fetch(`${API}/purchase-orders/${id}/cancel`, {
        method: 'POST',
        headers: { Authorization: headers.Authorization, 'x-tenant-slug': headers['x-tenant-slug'] },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al cancelar');
      }
      notify({ type: 'success', message: 'Orden cancelada' });
      await loadOrders();
    } catch (e: any) {
      notify({ type: 'error', message: 'No se pudo cancelar la orden' });
    } finally {
      setActionBusy(null);
      setPendingConfirm(null);
    }
  }

  function closePO(id: string) {
    setPendingConfirm({ action: 'close_po', poId: id });
  }

  async function executeClosePO(id: string) {
    setActionBusy(`close-${id}`);
    try {
      const res = await fetch(`${API}/purchase-orders/${id}/close`, {
        method: 'POST',
        headers: { Authorization: headers.Authorization, 'x-tenant-slug': headers['x-tenant-slug'] },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Error al cerrar');
      }
      notify({ type: 'success', message: 'Orden cerrada' });
      await loadOrders();
    } catch (e: any) {
      notify({ type: 'error', message: 'No se pudo cerrar la orden' });
    } finally {
      setActionBusy(null);
      setPendingConfirm(null);
    }
  }

  const counts = useMemo(() => {
    return {
      all: orders.length,
      draft: orders.filter((o) => o.status === 'draft').length,
      sent: orders.filter((o) => o.status === 'sent').length,
      partial_received: orders.filter((o) => o.status === 'partial_received' || o.status === 'partial').length,
      partial: 0,
      received: orders.filter((o) => o.status === 'received').length,
      closed: orders.filter((o) => o.status === 'closed').length,
      cancelled: orders.filter((o) => o.status === 'cancelled').length,
    };
  }, [orders]);

  const chips: { key: POStatus | 'all'; label: string }[] = [
    { key: 'all', label: 'Todas' },
    { key: 'draft', label: 'Borradores' },
    { key: 'sent', label: 'Enviadas' },
    { key: 'partial_received', label: 'Entregas parciales' },
    { key: 'received', label: 'Entregadas' },
    { key: 'closed', label: 'Cerradas' },
    { key: 'cancelled', label: 'Canceladas' },
  ];

  function canSend(status: POStatus) {
    return status === 'draft';
  }

  function canReceive(status: POStatus) {
    return ['draft', 'sent', 'partial_received', 'partial'].includes(status);
  }

  function canPay(status: POStatus) {
    return ['sent', 'partial_received', 'partial', 'received'].includes(status);
  }

  function canClose(status: POStatus, paymentStatus: string) {
    return ['partial_received', 'partial', 'received'].includes(status) && paymentStatus === 'paid';
  }

  function canCancel(status: POStatus) {
    return ['draft', 'sent'].includes(status);
  }

  function openSend(po: PurchaseOrder) {
    setSendAttachment(null);
    setSendChannels({
      sendEmail: po.sendEmail ?? true,
      sendWhatsapp: po.sendWhatsapp ?? true,
      notifyManagerWhatsapp: po.notifyManagerWhatsapp ?? false,
    });
    setSendTarget(po);
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Órdenes de Compra</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-1">
            {total} orden{total !== 1 ? 'es' : ''} en total
          </p>
        </div>
        <button
          onClick={() => {
            if (showForm) resetForm();
            setShowForm(!showForm);
          }}
          className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-5 py-4 font-bold text-base sm:text-lg shadow transition-colors min-h-[56px]"
        >
          <span className="text-xl">📋</span>
          {showForm ? 'Cancelar' : 'Nueva Orden'}
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 p-4 rounded-lg mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="text-sm font-medium">⚠️ {error}</span>
          <button
            onClick={loadStaticData}
            className="px-4 py-2 bg-red-700 text-white rounded-lg text-sm font-medium hover:bg-red-800 transition-colors min-h-[44px]"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <form className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 sm:p-6 mb-6" onSubmit={(e) => create(e, false)}>
          <h2 className="text-lg font-bold mb-4">📋 Nueva Orden de Compra</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Supplier dropdown */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Proveedor *</label>
              <select
                value={form.supplierId}
                onChange={(e) => {
                  const sid = e.target.value;
                  const sup = suppliers.find((s) => s.id === sid);
                  setForm((f) => ({
                    ...f,
                    supplierId: sid,
                    sendEmail: sup?.sendOcEmail ?? f.sendEmail,
                    sendWhatsapp: sup?.sendOcWhatsapp ?? f.sendWhatsapp,
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Seleccionar proveedor...</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              {selectedSupplier?.paymentTerms || selectedSupplier?.paymentMethod || selectedSupplier?.paymentDays !== undefined ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mt-1">Condición: {paymentConditionLabel(selectedSupplier)}</p>
              ) : null}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Fecha esperada</label>
              <input
                type="date"
                value={form.expectedDate}
                onChange={(e) => setForm((f) => ({ ...f, expectedDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Moneda</label>
              <select
                value={form.currency}
                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              >
                <option value="ARS">ARS ($)</option>
                <option value="USD">USD (US$)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Tipo de cambio</label>
              <input
                type="number"
                step="0.01"
                value={form.exchangeRate}
                disabled={form.currency === 'ARS'}
                onChange={(e) => setForm((f) => ({ ...f, exchangeRate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base disabled:bg-gray-100 dark:disabled:bg-gray-800"
              />
            </div>
          </div>

          {/* Items */}
          {/* Lectura automatica del documento que da origen a la orden */}
          <div className="mb-4 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap items-center gap-3">
              {!showImport ? (
                // Discreta a proposito: si vas a cargar los productos a mano, la
                // lectura de archivos no tiene por que ocupar lugar ni ofrecerse.
                <button
                  type="button"
                  onClick={() => setShowImport(true)}
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline min-h-[32px]"
                >
                  📄 Tengo el remito o el presupuesto: importar desde una foto o PDF
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => importInputRef.current?.click()}
                    disabled={importing}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white rounded-lg font-medium min-h-[44px] transition-colors"
                  >
                    {importing ? '⏳ Leyendo el documento…' : '📄 Elegir la foto o el PDF'}
                  </button>
                  <input
                    ref={importInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={importFromDocument}
                  />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Foto del remito o presupuesto: se completa el formulario y después lo revisás. Hasta 20 MB.
                  </span>
                </>
              )}
            </div>

            {importing && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Puede tardar de unos segundos a un par de minutos, según cuántos productos tenga el documento. Mientras tanto podés seguir cargando a mano.
              </p>
            )}

            {importError && (
              <p className="text-sm text-red-600 dark:text-red-400 mt-2">⚠️ {importError}</p>
            )}

            {importInfo && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-1">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  📄 Documento leído
                  {importInfo.documentNumber ? ` · Nro ${importInfo.documentNumber}` : ''}
                  {importInfo.documentDate ? ` · ${formatDate(importInfo.documentDate)}` : ''}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {importInfo.supplier
                    ? `Proveedor en el documento: ${importInfo.supplier}`
                    : 'No se pudo leer el proveedor del documento'}
                  {importInfo.total !== null && importInfo.total !== undefined
                    ? ` · Total: ${formatMoney(importInfo.total)}`
                    : ''}
                </p>

                {importInfo.needsAttention.length > 0 && (
                  <div className="text-xs text-amber-700 dark:text-amber-300 pt-1">
                    <p className="font-medium">Estos productos no están en tu stock:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {importInfo.needsAttention.map((n, i) => (
                        <li key={i}>
                          {n.description}
                          {n.kind === 'catalog'
                            ? ' — 🟢 está en el catálogo de tu rubro: agregalo al stock más abajo'
                            : ' — 🟠 no se encontró: revisá el texto y agregalo al stock más abajo'}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {importInfo.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-700 dark:text-amber-300">
                    ⚠️ {w}
                  </p>
                ))}

                <button
                  type="button"
                  onClick={openDocumentWindow}
                  className="mt-2 px-3 py-2 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white min-h-[36px]"
                >
                  🔍 Abrir el documento en otra ventana
                </button>

                {/* El proveedor: si el documento trae un nombre que no está en la lista,
                    se ofrece crearlo sin perder lo leído. Si no se leyó, se dice fuerte. */}
                {importInfo.supplier?.trim() && !form.supplierId ? (
                  <div className="mt-3 rounded-lg border-2 border-amber-400 bg-amber-50 px-3 py-3 dark:border-amber-600 dark:bg-amber-900/20">
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                      El proveedor «{importInfo.supplier.trim()}» no está en tu lista
                    </p>
                    <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
                      El documento lo dice, pero no existe como proveedor tuyo.
                      Creálo ahora y la orden sigue con él puesto.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setNewSupplier({ name: importInfo.supplier!.trim(), taxId: '', phone: '', email: '' });
                        setShowSupplierModal(true);
                      }}
                      className="mt-2 px-3 py-2 rounded-lg text-sm font-semibold bg-amber-600 hover:bg-amber-700 text-white min-h-[40px]"
                    >
                      ➕ Crear el proveedor «{importInfo.supplier.trim()}»
                    </button>
                  </div>
                ) : null}

                {!importInfo.supplier?.trim() && !form.supplierId ? (
                  <div className="mt-3 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2.5 dark:border-gray-600 dark:bg-gray-700/40">
                    <p className="text-base font-semibold text-gray-700 dark:text-gray-200">
                      No se pudo leer el proveedor
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      El documento no trae un nombre legible: elegilo a mano antes de guardar.
                    </p>
                  </div>
                ) : null}

                <p className="text-[11px] text-gray-400 dark:text-gray-500 pt-1">
                  La lectura es automática y puede tener errores. Revisá cantidades y precios antes de guardar.
                  El documento queda adjunto a la orden.
                </p>
              </div>
            )}
          </div>

          <div className="mb-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">Productos</label>
              {form.supplierId && (
                <label
                  className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer"
                  title="Si no encontrás el producto, destildá: puede que ya lo tengas con otro proveedor."
                >
                  <input
                    type="checkbox"
                    checked={onlySupplierProducts}
                    onChange={(e) => setOnlySupplierProducts(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                  Solo productos del proveedor
                </label>
              )}
            </div>
            {form.items.some((i) => i.productId && i.confidence === 'review' && !i.reviewed) && (
              <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-700 dark:bg-amber-900/20">
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  {form.items.filter((i) => i.productId && i.confidence === 'review' && !i.reviewed).length === 1
                    ? 'Una línea se parece a un producto pero el documento dice algo que ese producto no explica.'
                    : `${form.items.filter((i) => i.productId && i.confidence === 'review' && !i.reviewed).length} líneas se parecen a un producto pero el documento dice algo que ese producto no explica.`}{' '}
                  Revisalas una por una: puede ser otro artículo.
                </p>
              </div>
            )}
            <PendingLinesBar items={form.items} onRemove={removeUnresolvedItems} />
            <div className="space-y-3">
              {form.items.map((item, idx) => {
                const c = calcItem(item);
                const resolved = item.productId !== '';
                // ¿Esta línea viene de un documento leído? Si se cargó a mano, no hay
                // nada que mostrar de una lectura que no existió: va el buscador de
                // siempre, sin textos ni opciones de documento.
                const fromDocument = cameFromDocument(item);
                // Sin preselección, "verde" implicaría una seguridad que la lectura no
                // tiene: mientras no haya producto elegido, la línea está pendiente.
                const fromCatalog = item.matchKind === 'catalog' && !resolved;
                return (
                  <div
                    key={idx}
                    className={`rounded-lg border-2 ${
                      resolved ? 'border-transparent' : 'border-amber-400 dark:border-amber-600'
                    }`}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md items-end">
                    <div className="sm:col-span-4">
                      {!fromDocument && (
                        <ProductCombobox
                          label={`Producto ${idx + 1}`}
                          products={visibleProducts}
                          getStockInfo={getStockInfo}
                          salesData={salesData}
                          value={item.productId}
                          onSelect={(pid) => onProductChange(idx, pid)}
                        />
                      )}

                      {fromDocument && (
                      <>
                      {/* Lo que leyó el documento, tal cual. No es una selección: es un
                          dato. Preseleccionar un producto afirmaría una certeza que la
                          lectura no tiene. */}
                      <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2">
                        <p className="text-[10px] uppercase tracking-wide text-gray-400">Lo que se leyó</p>
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                          {item.description?.trim() || '(el documento no trajo descripción)'}
                        </p>
                      </div>
                      </>
                      )}

                      {!resolved && fromDocument && (
                        <>
                          <p className="mt-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                            ¿Cuál es el producto correcto?
                          </p>
                          <div className="mt-1 space-y-1">
                            {(item.candidates ?? []).map((cand, i) => {
                              // Semaforo del nivel de coincidencia: verde solo cuando
                              // el candidato explica todo lo que dice el documento (o
                              // vino por codigo de barras). La regla vive en
                              // purchaseOrderItemView.ts y esta testeada.
                              const tone = optionTone(cand);
                              const seguro = tone === 'green';
                              const dudoso = tone === 'amber';
                              const franja = seguro
                                ? 'border-l-green-500'
                                : dudoso
                                  ? 'border-l-amber-500'
                                  : 'border-l-gray-300 dark:border-l-gray-600';
                              const etiqueta = seguro
                                ? '🟢 coincide'
                                : dudoso
                                  ? '🟡 revisar'
                                  : '⚪ parecido';
                              return (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => chooseAlternative(idx, cand)}
                                  className={`block w-full text-left px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 border-l-4 ${franja} hover:bg-gray-100 dark:hover:bg-gray-600 text-xs min-h-[36px]`}
                                >
                                  <span className="font-medium text-gray-800 dark:text-gray-100">
                                    {cand.source === 'stock' ? '📦' : '🟢'} {cand.name}
                                  </span>
                                  {cand.brand && (
                                    <span className="text-gray-500 dark:text-gray-400"> · {cand.brand}</span>
                                  )}
                                  <span className="block text-[11px]">
                                    <span className={seguro ? 'text-green-700 dark:text-green-400' : dudoso ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}>
                                      {etiqueta}
                                    </span>
                                    <span className="text-gray-500 dark:text-gray-400">
                                      {' · '}{cand.source === 'stock' ? 'en tu stock' : 'del catálogo'}
                                      {dudoso && ` · el documento dice algo que este no explica: ${cand.missingWords.join(', ')}`}
                                    </span>
                                  </span>
                                </button>
                              );
                            })}
                            <button
                              type="button"
                              onClick={() => (creatingLine === idx ? setCreatingLine(null) : openCreate(idx))}
                              className="block w-full text-left px-2 py-1.5 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 text-xs font-medium text-gray-700 dark:text-gray-200 min-h-[36px]"
                            >
                              ➕ No está en ninguno: crearlo como producto nuevo
                            </button>
                            <button
                              type="button"
                              onClick={() => setManualSearch(manualSearch === idx ? null : idx)}
                              className="block w-full text-left px-2 py-1.5 rounded-lg text-[11px] text-blue-700 dark:text-blue-300 hover:underline min-h-[32px]"
                            >
                              🔎 Buscar otro producto en el stock
                            </button>
                            {manualSearch === idx && (
                              <div className="pt-1">
                                <ProductCombobox
                                  label={`Producto ${idx + 1}`}
                                  products={visibleProducts}
                                  getStockInfo={getStockInfo}
                                  salesData={salesData}
                                  value={item.productId}
                                  onSelect={(pid) => onProductChange(idx, pid)}
                                />
                              </div>
                            )}
                          </div>
                        </>
                      )}
                      {resolved && form.supplierId
                        && !(products.find((p) => p.id === item.productId)?.supplierIds || []).includes(form.supplierId) && (
                        <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 px-2 py-1.5 dark:border-blue-800 dark:bg-blue-900/20">
                          <p className="text-[11px] text-blue-700 dark:text-blue-300">
                            Este proveedor todavía no figura entre los proveedores de este producto:
                            puede que se lo estés empezando a comprar a él.
                          </p>
                          <button
                            type="button"
                            onClick={() => associateSupplierToProduct(idx, item.productId)}
                            disabled={associatingProductId === item.productId}
                            className="mt-1.5 px-2.5 py-1 rounded text-[11px] font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60 min-h-[32px]"
                          >
                            {associatingProductId === item.productId
                              ? 'Asociando...'
                              : 'Asociar este proveedor al producto'}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Cantidad</label>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base text-center"
                        required
                      />
                      {!(Number(item.quantity) > 0) && (
                        <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">
                          Sin cantidad: el documento no la trajo legible.
                        </p>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Costo unit.</label>
                      <input
                        type="number"
                        step="0.01"
                        value={item.unitCost}
                        onChange={(e) => updateItem(idx, 'unitCost', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base text-right"
                        required
                      />
                    </div>
                    <div className="sm:col-span-1">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">Desc. %</label>
                      <input
                        type="number"
                        step="0.01"
                        value={item.discountPct}
                        onChange={(e) => updateItem(idx, 'discountPct', e.target.value)}
                        className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base text-center"
                      />
                    </div>
                    <div className="sm:col-span-1">
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 dark:dark:text-gray-400 mb-1">IVA</label>
                      <select
                        value={item.taxRate}
                        onChange={(e) => updateItem(idx, 'taxRate', e.target.value)}
                        className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-center"
                      >
                        <option value="21">21%</option>
                        <option value="10.5">10,5%</option>
                        <option value="0">0%</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2 flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{formatMoney(c.total)}</span>
                      {form.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          title="Quitar esta línea del pedido"
                          className="text-red-600 hover:bg-red-50 px-2 py-1 rounded text-sm min-h-[44px]"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    </div>

                    {creatingLine === idx && (
                      <div className="mt-1 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-3">
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">
                          {item.matchKind === 'catalog' && item.catalogItemId
                            ? 'Agregar este artículo del catálogo a tu stock'
                            : 'Crear el producto'}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                          {item.matchKind === 'catalog' && item.catalogItemId && (
                            <p className="sm:col-span-4 text-[11px] text-gray-500 dark:text-gray-400">
                              La marca, el código de barras y las unidades se copian del catálogo de tu rubro.
                            </p>
                          )}
                          <div className="sm:col-span-2">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                              Nombre
                            </label>
                            <input
                              value={createForm.name}
                              onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
                            />
                          </div>
                          {!(item.matchKind === 'catalog' && item.catalogItemId) && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Marca
                              </label>
                              <input
                                value={createForm.brand}
                                onChange={(e) => setCreateForm((f) => ({ ...f, brand: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
                              />
                            </div>
                          )}
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                              Precio de venta
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={createForm.salePrice}
                              onChange={(e) => setCreateForm((f) => ({ ...f, salePrice: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base text-right"
                            />
                            <p className="mt-1 text-[11px] text-gray-400">
                              Sugerido: costo + 40%. Es obligatorio: en 0 se puede vender a $0.
                            </p>
                          </div>
                          {!(item.matchKind === 'catalog' && item.catalogItemId) && (
                            <div className="sm:col-span-2">
                              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Código de barras
                              </label>
                              <input
                                value={createForm.barcode}
                                onChange={(e) => setCreateForm((f) => ({ ...f, barcode: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
                              />
                            </div>
                          )}
                        </div>

                        {(suggestions.stock.length > 0 || suggestions.catalog.length > 0) && (
                          <div className="mt-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-2">
                            <p className="text-xs font-semibold text-blue-800 dark:text-blue-200 mb-1">
                              ¿Es alguno de estos? Usalo en vez de crear uno nuevo.
                            </p>
                            {suggestions.stock.map((s) => (
                              <button
                                key={s.id}
                                type="button"
                                onClick={() => {
                                  linkLineToProduct(idx, s.id);
                                  notify({ type: 'success', message: 'La línea quedó vinculada al producto que ya tenías.' });
                                }}
                                className="block w-full text-left text-xs px-2 py-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40"
                              >
                                📦 {s.name}{' '}
                                <span className="text-gray-500 dark:text-gray-400">— ya está en tu stock</span>
                                {/* Puede ser que estés agregando este proveedor al producto, y
                                    por eso no aparece entre sus proveedores todavía. */}
                                {form.supplierId && !s.supplierIds.includes(form.supplierId) && (
                                  <span className="text-blue-700 dark:text-blue-300">
                                    {' '}· sin este proveedor
                                  </span>
                                )}
                              </button>
                            ))}
                            {suggestions.catalog.map((s) => (
                              <button
                                key={s.id}
                                type="button"
                                disabled={createBusy}
                                onClick={() => createProductForLine(idx, { catalogItemId: s.id, name: s.name })}
                                className="block w-full text-left text-xs px-2 py-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-60"
                              >
                                🟢 {s.name} <span className="text-gray-500 dark:text-gray-400">— del catálogo, se agrega a tu stock</span>
                              </button>
                            ))}
                          </div>
                        )}

                        <div className="flex gap-3 mt-3">
                          <button
                            type="button"
                            onClick={() => setCreatingLine(null)}
                            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg font-semibold text-sm min-h-[44px]"
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            onClick={() => createProductForLine(idx)}
                            disabled={createBusy}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm disabled:opacity-70 min-h-[44px]"
                          >
                            {createBusy ? 'Creando...' : 'Crear y usar en la orden'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={addItem}
              className="mt-2 text-blue-600 dark:text-blue-400 font-semibold text-sm hover:underline min-h-[44px]"
            >
              + Agregar producto
            </button>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Flete</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.freight}
                  onChange={(e) => setForm((f) => ({ ...f, freight: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Desc. general %</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.generalDiscountPct}
                  onChange={(e) => setForm((f) => ({ ...f, generalDiscountPct: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
                />
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600 dark:text-gray-300">Subtotal</span>
                <span>{formatMoney(calc.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-red-600">Descuentos</span>
                <span className="text-red-600">-{formatMoney(calc.itemDiscount + genDiscount)}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-red-500 dark:text-red-400">IVA</span>
                <span className="text-red-500 dark:text-red-400">+{formatMoney(calc.taxTotal)}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600 dark:text-gray-300">Flete</span>
                <span>+{formatMoney(freight)}</span>
              </div>
              <div className="flex justify-between text-base font-bold border-t border-gray-200 dark:border-gray-700 pt-2 mt-2">
                <span>Total</span>
                <span>
                  {formatMoney(grandTotal)} {form.currency}
                </span>
              </div>
            </div>
          </div>

          <input
            type="text"
            placeholder="Notas (opcional)"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base mb-4"
          />

          {/* Canales de envío (snapshot de esta orden) */}
          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
              📨 Canales de envío de la OC
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Se copian de los preferidos del proveedor; podés cambiarlos para esta orden.
            </p>
            <div className="flex flex-col gap-2 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.sendEmail}
                  onChange={(e) => setForm((f) => ({ ...f, sendEmail: e.target.checked }))}
                  className="h-4 w-4"
                  disabled={!selectedSupplier?.email}
                />
                Enviar al proveedor por email
                {!selectedSupplier?.email && form.supplierId && (
                  <span className="text-xs text-red-500">(el proveedor no tiene email)</span>
                )}
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.sendWhatsapp}
                  onChange={(e) => setForm((f) => ({ ...f, sendWhatsapp: e.target.checked }))}
                  className="h-4 w-4"
                  disabled={!selectedSupplier?.phone}
                />
                Enviar al proveedor por WhatsApp
                {!selectedSupplier?.phone && form.supplierId && (
                  <span className="text-xs text-red-500">(el proveedor no tiene teléfono)</span>
                )}
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.notifyManagerWhatsapp}
                  onChange={(e) => setForm((f) => ({ ...f, notifyManagerWhatsapp: e.target.checked }))}
                  className="h-4 w-4"
                />
                Copia al manager/owner por WhatsApp
              </label>
            </div>
          </div>

          {/* Documentación de la orden */}
          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">📎 Documentación</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Adjuntá pedido, presupuesto o imagen de referencia (se convierte a webp automáticamente).
            </p>
            <AttachmentPicker
              label="Adjuntar documento"
              onFileSelected={(f) => setPendingDocFile(f)}
              onError={setError}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-70 min-h-[48px]"
            >
              {submitting ? 'Guardando...' : '💾 Guardar Borrador'}
            </button>
            <button
              type="button"
              onClick={(e) => create(e, true)}
              disabled={submitting}
              className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-70 min-h-[48px]"
            >
              {submitting ? 'Guardando...' : '📤 Guardar y Enviar'}
            </button>
          </div>
        </form>
      )}

      {/* Search and filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 mb-6">
        <input
          type="text"
          placeholder="🔍 Buscar por N° de orden o proveedor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
          aria-label="Buscar órdenes de compra"
        />
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar por estado">
          {chips.map((chip) => {
            const active = statusFilter === chip.key;
            return (
              <button
                key={chip.key}
                onClick={() => setStatusFilter(chip.key)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors min-h-[44px] ${
                  active ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200'
                }`}
                aria-pressed={active}
              >
                {chip.label} <span className="ml-1 opacity-80">(mostrando {counts[chip.key]})</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-8 text-center">
          <div className="inline-block h-8 w-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-3" />
          <p className="text-gray-600 dark:text-gray-300 font-medium">Cargando órdenes de compra...</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && orders.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-8 text-center">
          <p className="text-4xl mb-3">📭</p>
          <p className="text-gray-600 dark:text-gray-300 font-medium">
            {search || statusFilter !== 'all'
              ? 'No se encontraron órdenes con ese filtro.'
              : 'Aún no hay órdenes de compra. Creá la primera con el botón de arriba.'}
          </p>
        </div>
      )}

      {/* Mobile cards */}
      {!loading && orders.length > 0 && (
        <div className="grid grid-cols-1 lg:hidden gap-3 mb-6">
          {orders.map((po) => {
            const busy = actionBusy === `send-${po.id}` || actionBusy === `cancel-${po.id}` || actionBusy === `close-${po.id}`;
            return (
              <div key={po.id} className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <Link
                      to="/purchase-orders/$id"
                      params={{ id: po.id }}
                      className="font-mono font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {po.poNumber}
                    </Link>
                    <p className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{po.supplierName || 'Sin proveedor'}</p>
                  </div>
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                      statusColors[po.status] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {statusLabels[po.status] || po.status}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      paymentStatusColors[po.paymentStatus] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {paymentStatusLabels[po.paymentStatus] || po.paymentStatus}
                  </span>
                  {renderSendStatus(po)}
                  <span className="text-xs text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">{formatDate(po.expectedDate)}</span>
                </div>
                <p className="text-lg font-bold text-gray-900 mb-3">
                  {formatMoney(po.total)} {po.currency}
                </p>
                <div className="flex flex-wrap gap-2">
                  {canSend(po.status) && (
                    <button
                      onClick={() => openSend(po)}
                      disabled={busy}
                      className="flex-1 min-w-[80px] px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors disabled:opacity-70 min-h-[44px]"
                    >
                      📨 Enviar
                    </button>
                  )}
                  {canReceive(po.status) && (
                    <Link
                      to="/purchase-orders/$id"
                      params={{ id: po.id }}
                      className="flex-1 min-w-[80px] inline-flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 transition-colors min-h-[44px]"
                    >
                      📦 Recibir
                    </Link>
                  )}
                  {canPay(po.status) && (
                    <Link
                      to="/purchase-orders/$id"
                      params={{ id: po.id }}
                      className="flex-1 min-w-[80px] inline-flex items-center justify-center px-3 py-2 bg-purple-600 text-white rounded-lg text-xs font-semibold hover:bg-purple-700 transition-colors min-h-[44px]"
                    >
                      💰 Pagar
                    </Link>
                  )}
                  {canClose(po.status, po.paymentStatus) && (
                    <button
                      onClick={() => closePO(po.id)}
                      disabled={busy}
                      className="flex-1 min-w-[80px] px-3 py-2 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-70 min-h-[44px]"
                    >
                      {actionBusy === `close-${po.id}` ? '...' : '🔒 Cerrar'}
                    </button>
                  )}
                  {canCancel(po.status) && (
                    <button
                      onClick={() => cancelPO(po.id)}
                      disabled={busy}
                      className="flex-1 min-w-[80px] px-3 py-2 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-lg text-xs font-semibold hover:bg-red-200 transition-colors disabled:opacity-70 min-h-[44px]"
                    >
                      {actionBusy === `cancel-${po.id}` ? '...' : '✕ Cancelar'}
                    </button>
                  )}
                  <button
                    onClick={() => setAttachmentsFor(po)}
                    title="Documentos adjuntos"
                    className="flex-1 min-w-[80px] px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-h-[44px]"
                  >
                    📎 Docs
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Desktop table */}
      {!loading && orders.length > 0 && (
        <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">N° OC</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Proveedor</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">Total</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">Estado</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">Pago</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">Envío</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Fecha esperada</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((po) => {
                const busy = actionBusy === `send-${po.id}` || actionBusy === `cancel-${po.id}` || actionBusy === `close-${po.id}`;
                return (
                  <tr key={po.id} className="border-t hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3">
                      <Link
                        to="/purchase-orders/$id"
                      params={{ id: po.id }}
                        className="font-mono font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {po.poNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm">{po.supplierName || '-'}</td>
                    <td className="px-4 py-3 text-right text-sm font-semibold">
                      {formatMoney(po.total)} {po.currency}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                          statusColors[po.status] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {statusLabels[po.status] || po.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          paymentStatusColors[po.paymentStatus] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {paymentStatusLabels[po.paymentStatus] || po.paymentStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">{renderSendStatus(po)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{formatDate(po.expectedDate)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        {canSend(po.status) && (
                          <button
                            onClick={() => openSend(po)}
                            disabled={busy}
                            className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors disabled:opacity-70 min-h-[44px]"
                          >
                            📨 Enviar
                          </button>
                        )}
                        {canReceive(po.status) && (
                          <Link
                            to="/purchase-orders/$id"
                      params={{ id: po.id }}
                            className="px-3 py-2 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 transition-colors min-h-[44px] inline-flex items-center"
                          >
                            📦 Recibir
                          </Link>
                        )}
                        {canPay(po.status) && (
                          <Link
                            to="/purchase-orders/$id"
                      params={{ id: po.id }}
                            className="px-3 py-2 bg-purple-600 text-white rounded-lg text-xs font-semibold hover:bg-purple-700 transition-colors min-h-[44px] inline-flex items-center"
                          >
                            💰 Pagar
                          </Link>
                        )}
                        {canClose(po.status, po.paymentStatus) && (
                          <button
                            onClick={() => closePO(po.id)}
                            disabled={busy}
                            className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-70 min-h-[44px]"
                          >
                            {actionBusy === `close-${po.id}` ? '...' : '🔒 Cerrar'}
                          </button>
                        )}
                        {canCancel(po.status) && (
                          <button
                            onClick={() => cancelPO(po.id)}
                            disabled={busy}
                            className="px-3 py-2 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-lg text-xs font-semibold hover:bg-red-200 transition-colors disabled:opacity-70 min-h-[44px]"
                          >
                            {actionBusy === `cancel-${po.id}` ? '...' : '✕ Cancelar'}
                          </button>
                        )}
                        <button
                          onClick={() => setAttachmentsFor(po)}
                          title="Documentos adjuntos"
                          className="px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors min-h-[44px]"
                        >
                          📎
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Load more: single control for both layouts, before the footer summary.
          Appends the next batch of 50; hidden once loaded rows >= server total. */}
      {!loading && orders.length > 0 && orders.length < total && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={async () => {
              const nextPage = page + 1;
              setPage(nextPage);
              setLoadingMore(true);
              try {
                await loadOrders({ page: nextPage, append: true });
              } finally {
                setLoadingMore(false);
              }
            }}
            disabled={loadingMore}
            className="px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg font-semibold hover:bg-gray-200 transition-colors disabled:opacity-70 min-h-[44px]"
          >
            {loadingMore ? 'Cargando...' : 'Cargar más'}
          </button>
        </div>
      )}

      {/* Summary footer */}
      {!loading && orders.length > 0 && (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 dark:dark:text-gray-400">
          {orders.length} orden{orders.length !== 1 ? 'es' : ''}{' '}
          {statusFilter !== 'all' ? `(${statusLabels[statusFilter].toLowerCase()})` : ''}
        </p>
      )}

      {/* Modal: enviar orden con comprobante opcional */}
      {/* Alta del proveedor detectado en el documento, sin perder lo leído. */}
      <Modal
        open={showSupplierModal}
        onClose={() => setShowSupplierModal(false)}
        title="Nuevo proveedor"
      >
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Nombre</label>
            <input
              value={newSupplier.name}
              onChange={(e) => setNewSupplier((s) => ({ ...s, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">CUIT</label>
            <input
              value={newSupplier.taxId}
              onChange={(e) => setNewSupplier((s) => ({ ...s, taxId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Teléfono</label>
              <input
                value={newSupplier.phone}
                onChange={(e) => setNewSupplier((s) => ({ ...s, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Email</label>
              <input
                value={newSupplier.email}
                onChange={(e) => setNewSupplier((s) => ({ ...s, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-base"
              />
            </div>
          </div>
          <p className="text-[11px] text-gray-400">
            Después podés completar condiciones de pago y canales desde Proveedores.
          </p>
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={() => setShowSupplierModal(false)}
              className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg font-semibold min-h-[44px]"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={createSupplierFromDocument}
              disabled={supplierBusy}
              className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold disabled:opacity-70 min-h-[44px]"
            >
              {supplierBusy ? 'Creando…' : 'Crear proveedor'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={!!sendTarget}
        onClose={() => !sendSending && setSendTarget(null)}
        title={sendTarget ? `📨 Enviar orden ${sendTarget.poNumber}` : ''}
        maxWidth="max-w-md"
      >
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Confirmá el envío al proveedor. Podés ajustar los canales y adjuntar el comprobante (imagen o PDF).
        </p>
        <div className="mb-4 flex flex-col gap-2 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendChannels.sendEmail}
              onChange={(e) => setSendChannels((c) => ({ ...c, sendEmail: e.target.checked }))}
              className="h-4 w-4"
            />
            Email al proveedor
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendChannels.sendWhatsapp}
              onChange={(e) => setSendChannels((c) => ({ ...c, sendWhatsapp: e.target.checked }))}
              className="h-4 w-4"
            />
            WhatsApp al proveedor
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendChannels.notifyManagerWhatsapp}
              onChange={(e) =>
                setSendChannels((c) => ({ ...c, notifyManagerWhatsapp: e.target.checked }))
              }
              className="h-4 w-4"
            />
            Copia al manager/owner por WhatsApp
          </label>
        </div>
        <div className="mb-5">
          <AttachmentPicker
            label="Adjuntar comprobante de envío (opcional)"
            onFileSelected={(f) => setSendAttachment(f)}
            onError={setError}
          />
        </div>
        <div className="flex gap-3 justify-end">
          <button
            onClick={() => setSendTarget(null)}
            disabled={sendSending}
            className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-300 dark:border-gray-600 min-h-[44px]"
          >
            Cancelar
          </button>
          <button
            onClick={confirmSend}
            disabled={sendSending}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-70 min-h-[44px]"
          >
            {sendSending ? 'Enviando...' : '✓ Confirmar envío'}
          </button>
        </div>
      </Modal>

      {/* Modal: documentos de la orden */}
      <Modal
        open={!!attachmentsFor}
        onClose={() => setAttachmentsFor(null)}
        title={attachmentsFor ? `📎 Documentos · ${attachmentsFor.poNumber}` : ''}
        maxWidth="max-w-lg"
        showClose
      >
        {attachmentsFor && <AttachmentsPanel poId={attachmentsFor.id} onError={setError} />}
      </Modal>

      {/* Accessible confirm dialog: cancel/close */}
      {pendingConfirm && (
        <ConfirmModal
          open
          title={
            pendingConfirm.action === 'cancel_po'
              ? 'Cancelar orden de compra'
              : 'Cerrar orden de compra'
          }
          message={
            pendingConfirm.action === 'cancel_po'
              ? '¿Cancelar esta orden de compra?'
              : '¿Cerrar esta orden con lo recibido hasta ahora?'
          }
          confirmLabel={
            pendingConfirm.action === 'cancel_po' ? 'Sí, cancelar la orden' : 'Sí, cerrar la orden'
          }
          variant={pendingConfirm.action === 'cancel_po' ? 'danger' : 'default'}
          busy={
            actionBusy === `cancel-${pendingConfirm.poId}` ||
            actionBusy === `close-${pendingConfirm.poId}`
          }
          onCancel={() => setPendingConfirm(null)}
          onConfirm={() => {
            const pc = pendingConfirm;
            if (pc.action === 'cancel_po') executeCancelPO(pc.poId);
            else executeClosePO(pc.poId);
          }}
        />
      )}
    </div>
  );
}
